import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./Content";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
); 

const AveragePriceCalculator = () => {
  const content = article.find((item) => item.id === 4);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    initialShares: "100",
    initialPrice: "50.00",
    additionalShares: "50",
    additionalPrice: "45.00",
    totalInvested: "",
    totalShares: "",
    averagePrice: "",
    currency: "1",
    commission: "5.00"
  });

  const [purchases, setPurchases] = useState([
    { shares: "100", price: "50.00", commission: "5.00" },
    { shares: "50", price: "45.00", commission: "5.00" }
  ]);

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateAveragePrice();
    }
  }, [inputs, purchases, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handlePurchaseChange = (index, field, value) => {
    const updatedPurchases = purchases.map((purchase, i) => 
      i === index ? { ...purchase, [field]: value } : purchase
    );
    setPurchases(updatedPurchases);
  };

  const addPurchase = () => {
    setPurchases([...purchases, { shares: "", price: "", commission: "0.00" }]);
  };

  const removePurchase = (index) => {
    if (purchases.length > 1) {
      setPurchases(purchases.filter((_, i) => i !== index));
    }
  };

  const calculateAveragePrice = () => {
    let totalShares = 0;
    let totalCost = 0;
    let totalCommission = 0;

    // Calculate from individual purchases
    purchases.forEach(purchase => {
      const shares = parseFloat(purchase.shares) || 0;
      const price = parseFloat(purchase.price) || 0;
      const commission = parseFloat(purchase.commission) || 0;
      
      totalShares += shares;
      totalCost += shares * price;
      totalCommission += commission;
    });

    if (totalShares === 0) return;

    const totalInvestment = totalCost + totalCommission;
    const averagePrice = totalInvestment / totalShares;
    const currencyRate = parseFloat(inputs.currency) || 1;

    const resultData = {
      totalShares,
      totalInvestment: totalInvestment * currencyRate,
      totalCost: totalCost * currencyRate,
      totalCommission: totalCommission * currencyRate,
      averagePrice: averagePrice * currencyRate,
      breakEvenPrice: averagePrice * currencyRate,
      currencyRate
    };

    setResult(resultData);
  };

  const calculateDollarCostAverage = () => {
    const { monthlyInvestment, stockPrice, months, commission } = inputs;
    
    if (!monthlyInvestment || !stockPrice || !months) return;

    const monthly = parseFloat(monthlyInvestment);
    const price = parseFloat(stockPrice);
    const monthCount = parseInt(months);
    const comm = parseFloat(commission || 0);

    const dcaData = [];
    let totalShares = 0;
    let totalInvested = 0;

    for (let i = 1; i <= monthCount; i++) {
      const sharesBought = (monthly - comm) / price;
      totalShares += sharesBought;
      totalInvested += monthly;
      const averagePrice = totalInvested / totalShares;
      
      dcaData.push({
        month: i,
        shares: totalShares,
        invested: totalInvested,
        averagePrice,
        currentValue: totalShares * price
      });
    }

    return dcaData;
  };

  const resetCalculator = () => {
    setPurchases([
      { shares: "100", price: "50.00", commission: "5.00" },
      { shares: "50", price: "45.00", commission: "5.00" }
    ]);
    setInputs({
      initialShares: "100",
      initialPrice: "50.00",
      additionalShares: "50",
      additionalPrice: "45.00",
      totalInvested: "",
      totalShares: "",
      averagePrice: "",
      currency: "1",
      commission: "5.00",
      monthlyInvestment: "1000",
      stockPrice: "50.00",
      months: "12"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Total Shares,${result.totalShares}`,
      `Total Investment,${result.totalInvestment.toFixed(2)}`,
      `Total Cost (without commission),${result.totalCost.toFixed(2)}`,
      `Total Commission,${result.totalCommission.toFixed(2)}`,
      `Average Price,${result.averagePrice.toFixed(2)}`,
      `Break-even Price,${result.breakEvenPrice.toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'average-price-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      purchases: [...purchases],
      ...result
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setPurchases(calc.purchases);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        purchases: [
          { shares: "200", price: "50.00", commission: "10.00" },
          { shares: "100", price: "45.00", commission: "10.00" }
        ]
      },
      moderate: { 
        purchases: [
          { shares: "500", price: "50.00", commission: "15.00" },
          { shares: "300", price: "40.00", commission: "15.00" },
          { shares: "200", price: "35.00", commission: "15.00" }
        ]
      },
      aggressive: { 
        purchases: [
          { shares: "1000", price: "50.00", commission: "20.00" },
          { shares: "500", price: "30.00", commission: "20.00" },
          { shares: "300", price: "25.00", commission: "20.00" },
          { shares: "200", price: "20.00", commission: "20.00" }
        ]
      }
    };
    
    setPurchases(presets[preset].purchases);
    addNotification(`${preset} preset applied`, "info");
  };

  // Chart Data
  const costChartData = result ? {
    labels: ["Stock Cost", "Commission & Fees"],
    datasets: [{
      data: [result.totalCost, result.totalCommission],
      backgroundColor: ["#19692C", "#FF6B6B"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const purchaseChartData = purchases.length > 0 ? {
    labels: purchases.map((_, i) => `Purchase ${i + 1}`),
    datasets: [
      {
        label: 'Purchase Price',
        data: purchases.map(p => parseFloat(p.price) || 0),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: false,
        tension: 0.4
      },
      {
        label: 'Average Price',
        data: purchases.map(() => result?.averagePrice || 0),
        borderColor: "#FFD93D",
        backgroundColor: "rgba(255, 217, 61, 0.1)",
        borderDash: [5, 5],
        fill: false,
        tension: 0.4
      }
    ]
  } : null;

  const dcaData = calculateDollarCostAverage();
  const dcaChartData = dcaData ? {
    labels: dcaData.map(d => `Month ${d.month}`),
    datasets: [
      {
        label: 'Average Price',
        data: dcaData.map(d => d.averagePrice),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Shares Accumulated',
        data: dcaData.map(d => d.shares),
        borderColor: "#FFD93D",
        backgroundColor: "rgba(255, 217, 61, 0.1)",
        fill: false,
        yAxisID: 'y1',
        tension: 0.4
      }
    ]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Average Price Calculator</h1>
        </div>
        <p>Calculate your average stock price, dollar-cost averaging, and optimize your investment strategy</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className="profit positive">
                        ${calc.averagePrice?.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Average Price'}
              </button>
              <button 
                className={`tab ${activeTab === "dca" ? "active" : ""}`}
                onClick={() => setActiveTab("dca")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'DCA' : 'Dollar Cost Avg'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Quick Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Average Price Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-shopping-cart"></i>
                    Purchase History
                  </h3>
                  
                  {purchases.map((purchase, index) => (
                    <div key={index} className="purchase-group">
                      <div className="purchase-header">
                        <h4>Purchase #{index + 1}</h4>
                        {purchases.length > 1 && (
                          <button 
                            className="remove-purchase-btn"
                            onClick={() => removePurchase(index)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div><br/>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                          <input 
                            type="number" 
                            value={purchase.shares}
                            onChange={(e) => handlePurchaseChange(index, 'shares', e.target.value)}
                            placeholder="0"
                            min="0"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-dollar-sign"></i> Price per Share</label>
                          <input 
                            type="number" 
                            value={purchase.price}
                            onChange={(e) => handlePurchaseChange(index, 'price', e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-coins"></i> Commission</label>
                          <input 
                            type="number" 
                            value={purchase.commission}
                            onChange={(e) => handlePurchaseChange(index, 'commission', e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button className="btn add-purchase-btn" onClick={addPurchase}>
                    <i className="fas fa-plus"></i>
                    Add Another Purchase
                  </button>

                  <h3>
                    <i className="fas fa-cog"></i>
                    Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-globe"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currency" 
                        value={inputs.currency} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Dollar Cost Averaging Tab */}
          {activeTab === "dca" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-calendar-alt"></i>
                  Dollar Cost Averaging
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-dollar-sign"></i> Monthly Investment</label>
                    <input 
                      type="number" 
                      name="monthlyInvestment" 
                      value={inputs.monthlyInvestment} 
                      onChange={handleChange}
                      placeholder="1000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Stock Price</label>
                    <input 
                      type="number" 
                      name="stockPrice" 
                      value={inputs.stockPrice} 
                      onChange={handleChange}
                      placeholder="50.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-clock"></i> Months</label>
                    <input 
                      type="number" 
                      name="months" 
                      value={inputs.months} 
                      onChange={handleChange}
                      placeholder="12"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-coins"></i> Commission per Trade</label>
                    <input 
                      type="number" 
                      name="commission" 
                      value={inputs.commission} 
                      onChange={handleChange}
                      placeholder="5.00"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit positive">
                          <i className="fas fa-dollar-sign"></i>
                          ${calc.averagePrice?.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-layer-group"></i> Shares: {calc.totalShares}</span>
                        <span><i className="fas fa-money-bill-wave"></i> Invested: ${calc.totalInvestment?.toFixed(2)}</span>
                        <span><i className="fas fa-balance-scale"></i> Avg Price: ${calc.averagePrice?.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "dca") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-balance-scale"></i>
                    Average Price Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value positive">
                          ${result.averagePrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Average Price Per Share
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.totalShares}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-layer-group"></i>
                          Total Shares
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.totalInvestment.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Total Investment
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.breakEvenPrice.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-arrow-down"></i>
                          Break-even Price
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Total Stock Cost:</span>
                        <span>${result.totalCost.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Total Commission:</span>
                        <span>${result.totalCommission.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-globe"></i> Currency Rate:</span>
                        <span>{result.currencyRate}x</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-shopping-cart"></i> Number of Purchases:</span>
                        <span>{purchases.length}</span>
                      </div>
                    </div>

                    {/* Cost Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Cost Breakdown
                      </h3>
                      <Doughnut 
                        data={costChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Purchase History Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Purchase Price vs Average
                      </h3>
                      <Line 
                        data={purchaseChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: false,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toFixed(2);
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your purchase details to see calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "dca" && dcaData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Dollar Cost Averaging Results
                </h3>
                <div className="chart-container">
                  <Line 
                    data={dcaChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          type: 'linear',
                          display: true,
                          position: 'left',
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toFixed(2);
                            }
                          }
                        },
                        y1: {
                          type: 'linear',
                          display: true,
                          position: 'right',
                          grid: {
                            drawOnChartArea: false,
                          },
                        }
                      }
                    }}
                  />
                </div>
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      ${dcaData[dcaData.length - 1]?.averagePrice.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-balance-scale"></i>
                      Final Avg Price
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {dcaData[dcaData.length - 1]?.shares.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-layer-group"></i>
                      Total Shares
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${dcaData[dcaData.length - 1]?.invested.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-money-bill-wave"></i>
                      Total Invested
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${dcaData[dcaData.length - 1]?.currentValue.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Current Value
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default AveragePriceCalculator;